import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CustomTextField } from '../common/CustomTextFields';
import { PrimaryButton, SecondaryButton } from '../common/Buttons';
import {
    getServiceOrderCrew,
    addServiceOrderCrewMember,
    updateServiceOrderCrewMember,
    deleteServiceOrderCrewMember,
} from '../../services/serviceOrders';
import { getUsersByRole, getCertificateValidity } from '../../services/users';
import { toastApiError } from '../../utils/errorHandler';
import ConfirmDialog from '../common/ConfirmDialog';
import { filterTechniciansByServiceType, formatTechnicianOptionLabel, isTechnicianCertificateExpired } from '../../utils/technicianSkillUtils';

// Mirrors ServiceOrderCrewMember.JOB_FUNCTION_CHOICES (backend/core/service_orders/models.py)
const JOB_FUNCTION_OPTIONS = [
    { value: 'radiographer', label: 'Radiographer', rtOnly: true },
    { value: 'darkroom_photographer', label: 'Darkroom / Photographer', rtOnly: true },
    { value: 'interpreter', label: 'Interpreter', rtOnly: true },
    { value: 'technician', label: 'Technician', rtOnly: false },
];

const SHIFT_OPTIONS = [
    { value: '', label: 'Not specified' },
    { value: 'day', label: 'Day' },
    { value: 'night', label: 'Night' },
    { value: 'general', label: 'General' },
];

const emptyForm = { user: null, job_function: 'technician', work_date: '', shift: '', hours_worked: '', work_performed: '' };

const formatUserName = (user) => {
    if (!user) return 'Unknown';
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.email || user.username;
};

// Additional (non-Lead-Technician) crew roster — Radiographer/Darkroom/Interpreter
// for RT jobs, generic Technician for MT/PT/UT/VT. Record-keeping only: no
// notifications, no report-edit rights, surfaced separately on the Reflection
// Dashboard's Crew Work Log (not part of completion/turnaround metrics).
const ServiceOrderCrewSection = ({ serviceOrder }) => {
    const [crewMembers, setCrewMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [certValidityMap, setCertValidityMap] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState(null);

    const isRT = Boolean(serviceOrder?.service_type?.name?.startsWith('RT'));
    const availableJobFunctions = JOB_FUNCTION_OPTIONS.filter((opt) => !opt.rtOnly || isRT);

    const fetchCrew = useCallback(async () => {
        if (!serviceOrder?.id) return;
        setLoading(true);
        try {
            const data = await getServiceOrderCrew(serviceOrder.id);
            setCrewMembers(data);
        } catch (error) {
            toastApiError(toast.error, error, 'Failed to load crew members', 'view crew members');
        } finally {
            setLoading(false);
        }
    }, [serviceOrder?.id]);

    useEffect(() => {
        fetchCrew();
    }, [fetchCrew]);

    const openAddDialog = () => {
        setEditingId(null);
        setForm({ ...emptyForm, job_function: isRT ? 'radiographer' : 'technician' });
        setDialogOpen(true);
    };

    const openEditDialog = (member) => {
        setEditingId(member.id);
        setForm({
            user: member.user || null,
            job_function: member.job_function,
            work_date: member.work_date || '',
            shift: member.shift || '',
            hours_worked: member.hours_worked ?? '',
            work_performed: member.work_performed || '',
        });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    useEffect(() => {
        if (!dialogOpen || technicianOptions.length > 0) return;
        (async () => {
            try {
                const [users, validityMap] = await Promise.all([
                    getUsersByRole('Technician'),
                    getCertificateValidity({ role: 'Technician' }).catch(() => ({})),
                ]);
                setTechnicianOptions(users);
                setCertValidityMap(validityMap || {});
            } catch (error) {
                toastApiError(toast.error, error, 'Failed to load technicians', 'load technicians');
            }
        })();
    }, [dialogOpen, technicianOptions.length]);

    const handleSave = async () => {
        if (!form.user || !form.job_function || !form.work_date) {
            toast.error('Technician, job function, and work date are required.');
            return;
        }
        if (isTechnicianCertificateExpired(form.user, certValidityMap)) {
            toast.error('Cannot assign technician: their certificate has expired. Please renew first.');
            return;
        }
        setSaving(true);
        const payload = {
            service_order: serviceOrder.id,
            user_id: form.user.id,
            job_function: form.job_function,
            work_date: form.work_date,
            shift: form.shift || '',
            hours_worked: form.hours_worked === '' ? null : form.hours_worked,
            work_performed: form.work_performed || '',
        };
        try {
            if (editingId) {
                await updateServiceOrderCrewMember(editingId, payload);
                toast.success('Crew member updated.');
            } else {
                await addServiceOrderCrewMember(payload);
                toast.success('Crew member added.');
            }
            setDialogOpen(false);
            await fetchCrew();
        } catch (error) {
            toastApiError(toast.error, error, 'Failed to save crew member', 'save crew member');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (memberId) => {
        setMemberToDelete(memberId);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!memberToDelete) return;
        try {
            await deleteServiceOrderCrewMember(memberToDelete);
            toast.success('Crew member removed.');
            await fetchCrew();
        } catch (error) {
            toastApiError(toast.error, error, 'Failed to remove crew member', 'remove crew member');
        } finally {
            setMemberToDelete(null);
            setDeleteConfirmOpen(false);
        }
    };

    const jobFunctionLabel = (value) => JOB_FUNCTION_OPTIONS.find((opt) => opt.value === value)?.label || value;

    if (!serviceOrder?.id) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Crew / Additional Technicians
                </Typography>
                <SecondaryButton startIcon={<AddIcon />} size="small" onClick={openAddDialog}>
                    Add crew member
                </SecondaryButton>
            </Box>
            <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : crewMembers.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No additional crew logged yet. Only the Lead Technician above is tracked by default.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Technician</TableCell>
                                    <TableCell>Job Function</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Shift</TableCell>
                                    <TableCell>Hours</TableCell>
                                    <TableCell>Work Performed</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {crewMembers.map((member) => (
                                    <TableRow key={member.id}>
                                        <TableCell>{formatUserName(member.user)}</TableCell>
                                        <TableCell>{member.job_function_display || jobFunctionLabel(member.job_function)}</TableCell>
                                        <TableCell>{member.work_date}</TableCell>
                                        <TableCell>{member.shift || '-'}</TableCell>
                                        <TableCell>{member.hours_worked ?? '-'}</TableCell>
                                        <TableCell>{member.work_performed || '-'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => openEditDialog(member)} aria-label="Edit crew member">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteClick(member.id)} aria-label="Remove crew member">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit Crew Member' : 'Add Crew Member'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            {(() => {
                                const qualifiedTechnicians = filterTechniciansByServiceType(technicianOptions, serviceOrder?.service_type);
                                return (
                                    <Autocomplete
                                        options={qualifiedTechnicians}
                                        getOptionLabel={(option) => formatTechnicianOptionLabel(option, certValidityMap)}
                                        getOptionDisabled={(option) => isTechnicianCertificateExpired(option, certValidityMap)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={form.user}
                                        onChange={(_, newValue) => {
                                            if (newValue && isTechnicianCertificateExpired(newValue, certValidityMap)) {
                                                toast.error('Cannot assign technician: certificate has expired. Please renew first.');
                                                return;
                                            }
                                            setForm((f) => ({ ...f, user: newValue }));
                                        }}
                                        renderInput={(params) => (
                                            <CustomTextField
                                                {...params}
                                                label="Technician"
                                                required
                                                fullWidth
                                                helperText={
                                                    serviceOrder?.service_type
                                                        ? (qualifiedTechnicians.length === 0
                                                            ? `No technicians qualified for ${serviceOrder.service_type.name || 'this service order'}`
                                                            : `Showing technicians qualified for ${serviceOrder.service_type.name || 'this service order'} (${qualifiedTechnicians.length} available)`)
                                                        : undefined
                                                }
                                            />
                                        )}
                                    />
                                );
                            })()}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Job Function</InputLabel>
                                <Select
                                    label="Job Function"
                                    value={form.job_function}
                                    onChange={(e) => setForm((f) => ({ ...f, job_function: e.target.value }))}
                                >
                                    {availableJobFunctions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                label="Work Date"
                                type="date"
                                required
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.work_date}
                                onChange={(e) => setForm((f) => ({ ...f, work_date: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Shift</InputLabel>
                                <Select
                                    label="Shift"
                                    value={form.shift}
                                    onChange={(e) => setForm((f) => ({ ...f, shift: e.target.value }))}
                                >
                                    {SHIFT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                label="Hours Worked"
                                type="number"
                                fullWidth
                                inputProps={{ step: '0.25', min: 0 }}
                                value={form.hours_worked}
                                onChange={(e) => setForm((f) => ({ ...f, hours_worked: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomTextField
                                label="Work Performed"
                                multiline
                                minRows={2}
                                fullWidth
                                value={form.work_performed}
                                onChange={(e) => setForm((f) => ({ ...f, work_performed: e.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={closeDialog} disabled={saving}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setMemberToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Remove Crew Member"
                message="Are you sure you want to remove this crew member entry?"
                confirmText="Remove"
                cancelText="Cancel"
                severity="error"
            />
        </Box>
    );
};

export default ServiceOrderCrewSection;
